import type { NodeMetrics, EdgeMetrics } from '@/domain/metrics/contracts';
import { NodeMetricsSchema, EdgeMetricsSchema } from '@/domain/metrics/contracts';
import { propagateMetrics, type PropagationResult } from '@/domain/metrics/propagation/propagate';
import { aggregateFunnelMetrics, type FunnelAggregateResult, type AggregationNodeInput } from '@/domain/metrics/aggregation/funnel-metrics';
import { getNodeDefinition } from '../nodes/registry/node-registry';
import type { EditorGraph } from '../domain/editor-graph';

function readNodeMetrics(data: Record<string, unknown>): NodeMetrics {
  const parsed = NodeMetricsSchema.safeParse(data.metrics);
  return parsed.success ? parsed.data : {};
}

function readEdgeMetrics(data: Record<string, unknown> | undefined): EdgeMetrics {
  const parsed = EdgeMetricsSchema.safeParse(data?.metrics);
  return parsed.success ? parsed.data : {};
}

/**
 * SPEC-008 plan §29/T078 — the bridge between the React-facing EditorGraph
 * and the framework-free `src/domain/metrics` engine. This is the only
 * place that reads the Node Registry's roles/supportedMetrics and hands
 * plain data into the pure propagation/aggregation functions.
 */
export interface GraphMetricsResult {
  propagation: PropagationResult;
  funnel: FunnelAggregateResult;
}

export function evaluateGraphMetrics(graph: EditorGraph): GraphMetricsResult {
  const propagationInput = {
    nodes: graph.nodes.map((node) => ({
      id: node.id,
      metrics: readNodeMetrics(node.data as Record<string, unknown>),
    })),
    edges: graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      metrics: readEdgeMetrics(edge.data as Record<string, unknown> | undefined),
    })),
  };

  const propagation = propagateMetrics(propagationInput);

  const aggregationNodes: AggregationNodeInput[] = graph.nodes.map((node) => {
    const definition = getNodeDefinition(node.type ?? '');
    const metrics = readNodeMetrics(node.data as Record<string, unknown>);
    const roles = [...(definition?.metricRoles ?? []), ...(metrics.roleOverrides ?? [])];
    return {
      id: node.id,
      roles: [...new Set(roles)],
      result: propagation.nodeResults.get(node.id)!,
      includeInFunnelInvestment: metrics.includeInFunnelInvestment,
    };
  });

  const funnel = aggregateFunnelMetrics(aggregationNodes);

  return { propagation, funnel };
}
